/**
 * useActiveProject -- single source of truth for "which project is loaded"
 * across the entire app, regardless of route.
 *
 * Background
 * ----------
 * The active project today is tracked via two localStorage keys:
 *   - ``ugp_active_project_id``   -- session id (GUID), used by APIs
 *   - ``ugp_active_project_key``  -- registry name (e.g. "MTU"), used
 *                                    by manifest endpoints and the
 *                                    Hub wheel
 *
 * They get written from the planner (``App.tsx``) when a project is
 * loaded; they get cleared piecemeal by individual pages (e.g.
 * LoadProfile8760Page when the user clears the session id field).
 * No React context exists today, so cross-route reads have to poll
 * localStorage and there's no live signal when the project changes.
 *
 * This hook closes that gap:
 *  - reads the two keys synchronously at mount,
 *  - subscribes to the browser-native ``storage`` event for cross-tab
 *    updates (e.g. user has the planner open in tab A, opens 8760 in
 *    tab B; tab B sees the project),
 *  - subscribes to a custom ``ugp:active-project-change`` event for
 *    same-tab updates (the ``storage`` event does NOT fire in the tab
 *    that wrote the value -- only in OTHER tabs -- so we need a
 *    custom event to propagate within the same tab too),
 *  - exposes ``setActive(id, name)`` and ``clear()`` helpers that
 *    write the localStorage keys AND emit the custom event so the
 *    rest of the app picks up the change immediately.
 *
 * Pages that already write the localStorage keys (App.tsx,
 * UGridNetDesignModal, DesignGrid, DesignPlant, LoadProfile8760Page)
 * should ALSO call ``emitActiveProjectChange()`` so the indicator
 * updates in real time -- otherwise users see a stale name until
 * they navigate.
 */
import { useCallback, useEffect, useState } from 'react';

export const ACTIVE_PROJECT_ID_KEY = 'ugp_active_project_id';
export const ACTIVE_PROJECT_NAME_KEY = 'ugp_active_project_key';

/** Registry key of the active tutorial sandbox. Written by TutorialContext
 *  ONLY while a training tour is active, cleared when the tour stops. Its
 *  presence means "a guided tour is driving a disposable sandbox project", so
 *  it takes priority in {@link resolveActiveProjectKey} -- otherwise tutorial
 *  steps (Exclusions, Design Grid, the uGridNET modal) operate on whatever the
 *  user last loaded instead of the sandbox the tour just created. */
export const ACTIVE_TUTORIAL_SANDBOX_KEY = 'ugp_tutorial_sandbox_key';

/** Mirror the tutorial sandbox registry key to localStorage so non-context
 *  resolvers can target it. Pass '' / null to clear. */
export function setTutorialSandboxKey(name: string | null): void {
  try {
    if (name) localStorage.setItem(ACTIVE_TUTORIAL_SANDBOX_KEY, name);
    else localStorage.removeItem(ACTIVE_TUTORIAL_SANDBOX_KEY);
  } catch { /* noop */ }
}

/**
 * Resolve the registry key (e.g. "TST_minigrid") that project-scoped pages
 * should act on, with priority:
 *   1. active tutorial sandbox  (only set while a guided tour runs)
 *   2. ``ugp_active_project_key`` (the normally-loaded project)
 *   3. ``?project=`` URL parameter (deep links)
 * Returns '' when none is set. Shared by Exclusions, Design Grid and the
 * uGridNET modal so they agree on the target during tutorials (RCA 2026-06-15:
 * exclusions saved to / loaded from the wrong project mid-tour).
 */
export function resolveActiveProjectKey(): string {
  try {
    const sandbox = localStorage.getItem(ACTIVE_TUTORIAL_SANDBOX_KEY);
    if (sandbox) return sandbox;
  } catch { /* private mode */ }
  try {
    const active = localStorage.getItem(ACTIVE_PROJECT_NAME_KEY);
    if (active) return active;
  } catch { /* private mode */ }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('project');
    if (fromUrl) return fromUrl;
  } catch { /* SSR / non-DOM */ }
  return '';
}

/** Custom event name fired whenever the active project changes within
 *  the SAME tab (storage events only cover OTHER tabs). */
export const ACTIVE_PROJECT_EVENT = 'ugp:active-project-change';

export interface ActiveProject {
  /** GUID session id; '' when no project is loaded. */
  projectId: string;
  /** Registry name like 'MTU' or 'Mathebe'; '' when no project is loaded. */
  projectName: string;
  /** True when both id and name are set (i.e. a real project session is active). */
  isSet: boolean;
}

function readActiveFromStorage(): ActiveProject {
  let projectId = '';
  let projectName = '';
  try {
    projectId = localStorage.getItem(ACTIVE_PROJECT_ID_KEY) || '';
  } catch { /* private mode etc */ }
  try {
    projectName = localStorage.getItem(ACTIVE_PROJECT_NAME_KEY) || '';
  } catch { /* private mode etc */ }
  return {
    projectId,
    projectName,
    isSet: Boolean(projectId && projectName),
  };
}

/**
 * Write the two active-project keys + emit the change event so all
 * mounted ``useActiveProject`` consumers re-render immediately.
 *
 * Pass empty strings to clear (or call ``clearActiveProject()``).
 */
export function setActiveProject(id: string, name: string): void {
  try {
    if (id) localStorage.setItem(ACTIVE_PROJECT_ID_KEY, id);
    else localStorage.removeItem(ACTIVE_PROJECT_ID_KEY);
  } catch { /* noop */ }
  try {
    if (name) localStorage.setItem(ACTIVE_PROJECT_NAME_KEY, name);
    else localStorage.removeItem(ACTIVE_PROJECT_NAME_KEY);
  } catch { /* noop */ }
  emitActiveProjectChange();
}

/** Convenience wrapper for ``setActiveProject('', '')``. */
export function clearActiveProject(): void {
  setActiveProject('', '');
}

/**
 * Notify same-tab listeners that the active project changed.
 *
 * Pages that already write the localStorage keys directly (without
 * going through ``setActiveProject``) should call this immediately
 * after the write so the indicator updates without a page reload.
 *
 * Cross-tab updates work automatically via the browser's native
 * 'storage' event -- no need to emit anything for those.
 */
export function emitActiveProjectChange(): void {
  try {
    window.dispatchEvent(new CustomEvent(ACTIVE_PROJECT_EVENT));
  } catch { /* SSR / non-DOM env */ }
}

/**
 * React hook that returns the current active project + a stable
 * setter / clearer pair. Re-renders whenever the project changes
 * (same tab via custom event, other tabs via 'storage' event).
 */
export function useActiveProject(): ActiveProject & {
  setActive: (id: string, name: string) => void;
  clear: () => void;
} {
  const [active, setActive] = useState<ActiveProject>(() => readActiveFromStorage());

  useEffect(() => {
    const refresh = () => setActive(readActiveFromStorage());
    const handleStorage = (e: StorageEvent) => {
      // Only refresh when the relevant keys change -- avoids spurious
      // re-renders when other localStorage writes happen.
      if (
        e.key === ACTIVE_PROJECT_ID_KEY
        || e.key === ACTIVE_PROJECT_NAME_KEY
        || e.key === null  // null = storage cleared
      ) {
        refresh();
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener(ACTIVE_PROJECT_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(ACTIVE_PROJECT_EVENT, refresh);
    };
  }, []);

  const setActiveCb = useCallback((id: string, name: string) => {
    setActiveProject(id, name);
  }, []);
  const clearCb = useCallback(() => {
    clearActiveProject();
  }, []);

  return { ...active, setActive: setActiveCb, clear: clearCb };
}
